import React, { useEffect, useState, useCallback } from 'react';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
    AreaChart, Area, CartesianGrid, PieChart, Pie, Cell, Legend
} from 'recharts';
import { socket } from '../App';

const CAT_COLORS: Record<string, string> = {
    social:    '#6366f1',
    streaming: '#f59e0b',
    ads:       '#ef4444',
    tracking:  '#f97316',
    search:    '#3b82f6',
    news:      '#10b981',
    shopping:  '#ec4899',
    tech:      '#8b5cf6',
    system:    '#94a3b8',
    other:     '#64748b',
};

const CAT_LABEL: Record<string, string> = {
    social: '📱 Social', streaming: '🎬 Streaming', ads: '📢 Pub',
    tracking: '🔍 Tracking', search: '🔎 Recherche', news: '📰 Actu',
    shopping: '🛒 Shopping', tech: '💻 Tech', system: '⚙️ Système', other: '🌐 Autre',
};

interface DnsEvent {
    id: number;
    device_ip: string;
    domain: string;
    category: string;
    source: string;
    timestamp: number;
}

interface Device {
    device_ip: string;
    total: number;
    last_seen: number;
}

// Noms lisibles pour les IPs connues
const DEVICE_NAMES: Record<string, string> = {
    '192.168.1.11': '💻 PC',
    '192.168.1.14': '📺 TV',
};

function deviceLabel(ip: string) {
    return DEVICE_NAMES[ip] ? `${DEVICE_NAMES[ip]} (${ip})` : ip;
}

function timeAgo(ts: number) {
    const s = Math.floor((Date.now() - ts) / 1000);
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    return `${Math.floor(s / 3600)}h`;
}

export function DnsTab() {
    const [devices, setDevices] = useState<Device[]>([]);
    const [selectedIp, setSelectedIp] = useState<string>('all');
    const [hours, setHours] = useState(24);
    const [events, setEvents] = useState<DnsEvent[]>([]);
    const [topDomains, setTopDomains] = useState<any[]>([]);
    const [byHour, setByHour] = useState<any[]>([]);
    const [categories, setCategories] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [liveEvents, setLiveEvents] = useState<DnsEvent[]>([]);

    const load = useCallback(async () => {
        setLoading(true);
        const ip = selectedIp !== 'all' ? `&ip=${selectedIp}` : '';
        try {
            const [evRes, topRes, hourRes, catRes, devRes] = await Promise.all([
                fetch(`/api/dns/events?hours=${hours}${ip}`, { credentials: 'include' }),
                fetch(`/api/dns/top?hours=${hours}${ip}`, { credentials: 'include' }),
                fetch(`/api/dns/by-hour?hours=${hours}${ip}`, { credentials: 'include' }),
                fetch(`/api/dns/categories?hours=${hours}${ip}`, { credentials: 'include' }),
                fetch('/api/dns/devices', { credentials: 'include' }),
            ]);
            setEvents(await evRes.json());
            setTopDomains(await topRes.json());
            setByHour(await hourRes.json());
            setCategories(await catRes.json());
            setDevices(await devRes.json());
        } catch {}
        setLoading(false);
    }, [selectedIp, hours]);

    useEffect(() => { load(); }, [load]);

    // Rafraîchir les stats toutes les 30s
    useEffect(() => {
        const t = setInterval(load, 30_000);
        return () => clearInterval(t);
    }, [load]);

    // Flux temps réel via socket
    useEffect(() => {
        if (!socket) return;
        const onDns = (e: DnsEvent) => {
            if (selectedIp !== 'all' && e.device_ip !== selectedIp) return;
            setLiveEvents(prev => [{ ...e, timestamp: Date.now() }, ...prev].slice(0, 50));
        };
        socket.on('dns-event', onDns);
        return () => { socket.off('dns-event', onDns); };
    }, [selectedIp]);

    const totalRequests = events.length;
    const uniqueDomains = new Set(events.map(e => e.domain)).size;
    const lastSeen = events[0]?.timestamp;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Filtres */}
            <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', border: '1px solid #e5e7eb' }} className="filter-bar">
                <span style={{ fontWeight: 700, fontSize: 16 }}>🌐 Réseau DNS</span>
                <select
                    value={selectedIp}
                    onChange={e => setSelectedIp(e.target.value)}
                    style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, background: '#f9fafb' }}
                >
                    <option value="all">Tous les appareils</option>
                    {devices.map(d => (
                        <option key={d.device_ip} value={d.device_ip}>{deviceLabel(d.device_ip)} — {d.total.toLocaleString()} req</option>
                    ))}
                </select>
                <select
                    value={hours}
                    onChange={e => setHours(Number(e.target.value))}
                    style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, background: '#f9fafb' }}
                >
                    <option value={1}>1 heure</option>
                    <option value={6}>6 heures</option>
                    <option value={24}>24 heures</option>
                    <option value={72}>3 jours</option>
                    <option value={168}>7 jours</option>
                </select>
                <button onClick={load} disabled={loading}
                    style={{ padding: '6px 14px', borderRadius: 8, border: 'none', background: '#6366f1', color: '#fff', fontWeight: 600, cursor: loading ? 'wait' : 'pointer', fontSize: 13 }}>
                    {loading ? '...' : '↺ Refresh'}
                </button>
                {/* KPIs */}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 20 }}>
                    <Kpi label="Requêtes" value={totalRequests.toLocaleString()} />
                    <Kpi label="Domaines uniques" value={uniqueDomains.toLocaleString()} />
                    {lastSeen && <Kpi label="Dernière vue" value={timeAgo(lastSeen)} />}
                </div>
            </div>

            <div className="grid-2col">
                {/* Timeline activité */}
                <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', border: '1px solid #e5e7eb', gridColumn: '1 / -1' }}>
                    <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#374151' }}>Activité par heure</h3>
                    <ResponsiveContainer width="100%" height={160}>
                        <AreaChart data={byHour}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                            <XAxis dataKey="hour" tick={{ fontSize: 10 }} tickFormatter={v => v.slice(11, 16)} />
                            <YAxis tick={{ fontSize: 10 }} width={30} />
                            <Tooltip formatter={(v: any, n: any) => [v, n === 'count' ? 'Requêtes' : 'Domaines uniques']}
                                     labelFormatter={(l: any) => `${l}`} />
                            <Area type="monotone" dataKey="count" stroke="#6366f1" fill="#e0e7ff" name="count" />
                            <Area type="monotone" dataKey="unique_domains" stroke="#10b981" fill="#d1fae5" name="unique_domains" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>

                {/* Top domaines */}
                <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', border: '1px solid #e5e7eb' }}>
                    <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#374151' }}>Top domaines</h3>
                    <ResponsiveContainer width="100%" height={220}>
                        <BarChart data={topDomains.slice(0, 15)} layout="vertical">
                            <XAxis type="number" tick={{ fontSize: 10 }} />
                            <YAxis type="category" dataKey="domain" tick={{ fontSize: 10 }} width={130} />
                            <Tooltip />
                            <Bar dataKey="count" name="Requêtes">
                                {topDomains.slice(0, 15).map((entry, i) => (
                                    <Cell key={i} fill={CAT_COLORS[entry.category] || '#6366f1'} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Catégories */}
                <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', border: '1px solid #e5e7eb' }}>
                    <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#374151' }}>Catégories</h3>
                    {categories.length === 0
                        ? <p style={{ color: '#9ca3af', fontSize: 13, textAlign: 'center', marginTop: 40 }}>Aucune donnée</p>
                        : <ResponsiveContainer width="100%" height={220}>
                            <PieChart>
                                <Pie data={categories} dataKey="count" nameKey="category" cx="50%" cy="50%" outerRadius={80} label={(props: any) => `${CAT_LABEL[props.category] || props.category} ${((props.percent || 0) * 100).toFixed(0)}%`}>
                                    {categories.map((entry, i) => (
                                        <Cell key={i} fill={CAT_COLORS[entry.category] || '#64748b'} />
                                    ))}
                                </Pie>
                                <Tooltip formatter={(v: any, n: any) => [v, CAT_LABEL[n] || n]} />
                            </PieChart>
                        </ResponsiveContainer>
                    }
                </div>
            </div>

            {/* Flux live + historique */}
            <div style={{ background: '#fff', borderRadius: 12, padding: '16px 20px', border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                    <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#374151' }}>Flux temps réel</h3>
                    {liveEvents.length > 0 && (
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981', display: 'inline-block', boxShadow: '0 0 0 3px #d1fae5' }} />
                    )}
                    <span style={{ fontSize: 12, color: '#9ca3af', marginLeft: 'auto' }}>
                        {liveEvents.length} événements live | {events.length} historique
                    </span>
                </div>
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                        <thead>
                            <tr style={{ background: '#f9fafb' }}>
                                {['Heure', 'Device', 'Domaine', 'Catégorie', 'Source'].map(h => (
                                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: '#374151', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {[...liveEvents, ...events].slice(0, 100).map((e, i) => (
                                <tr key={`${e.id}-${i}`} style={{ borderBottom: '1px solid #f3f4f6', background: i < liveEvents.length ? '#f0fdf4' : undefined }}>
                                    <td style={{ padding: '6px 12px', color: '#6b7280', whiteSpace: 'nowrap' }}>
                                        {new Date(e.timestamp).toLocaleTimeString('fr-FR')}
                                    </td>
                                    <td style={{ padding: '6px 12px', color: '#374151', fontFamily: 'monospace', fontSize: 11 }}>
                                        {DEVICE_NAMES[e.device_ip] || e.device_ip}
                                    </td>
                                    <td style={{ padding: '6px 12px', color: '#1e40af', fontWeight: 500 }}>{e.domain}</td>
                                    <td style={{ padding: '6px 12px' }}>
                                        <span style={{
                                            padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 600,
                                            background: CAT_COLORS[e.category] + '22',
                                            color: CAT_COLORS[e.category] || '#64748b'
                                        }}>
                                            {CAT_LABEL[e.category] || e.category}
                                        </span>
                                    </td>
                                    <td style={{ padding: '6px 12px', color: '#9ca3af', fontSize: 11 }}>
                                        {e.source === 'sni' ? '🔒 TLS' : '📡 DNS'}
                                    </td>
                                </tr>
                            ))}
                            {events.length === 0 && liveEvents.length === 0 && (
                                <tr>
                                    <td colSpan={5} style={{ padding: '32px', textAlign: 'center', color: '#9ca3af' }}>
                                        Aucune donnée — le daemon bettercap est-il lancé ?
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function Kpi({ label, value }: { label: string; value: string }) {
    return (
        <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#1e293b' }}>{value}</div>
            <div style={{ fontSize: 11, color: '#9ca3af' }}>{label}</div>
        </div>
    );
}
